import { useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "fta_admin_secret";

// Protected admin page. The secret is entered by the user and only stored in
// sessionStorage while the tab is open. It is never rendered in page markup,
// never included in URLs or query strings, and is only sent via the
// X-Admin-Secret request header. The backend returns 404 on any bad or
// missing secret so this page's existence is not leaked publicly.
export default function AdminSubscribers() {
  const [secret, setSecret] = useState(() => {
    if (typeof window === "undefined") return "";
    return sessionStorage.getItem(STORAGE_KEY) || "";
  });
  const [subscribers, setSubscribers] = useState(null);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (secretToUse) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/subscribers`, {
        headers: { "X-Admin-Secret": secretToUse },
      });
      setSubscribers(res.data.subscribers || []);
      setCount(res.data.count || 0);
      sessionStorage.setItem(STORAGE_KEY, secretToUse);
    } catch (e) {
      setSubscribers(null);
      setError("Access denied. Check the admin secret and try again.");
      sessionStorage.removeItem(STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!secret.trim()) return;
    load(secret.trim());
  };

  const downloadCsv = async () => {
    setError("");
    try {
      const res = await axios.get(`${API}/admin/subscribers.csv`, {
        headers: { "X-Admin-Secret": secret.trim() },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      a.download = `subscribers-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError("Download failed. Verify the admin secret.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setSecret("");
    setSubscribers(null);
    setCount(0);
    setError("");
  };

  return (
    <div
      data-testid="admin-subscribers-page"
      className="min-h-screen bg-black text-white px-4 md:px-10 py-16 md:py-24"
    >
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#F4C10A] mb-2">
            Private · Restricted
          </div>
          <h1 className="font-wide uppercase text-3xl md:text-5xl leading-[0.95]">
            Subscriber Console
          </h1>
          <p className="mt-3 font-body text-white/60 text-sm md:text-base max-w-2xl">
            Enter the admin secret to view the current subscriber list and
            export the CSV. Access is verified server-side.
          </p>
        </div>

        {subscribers === null ? (
          <form
            data-testid="admin-login-form"
            onSubmit={onSubmit}
            className="flex flex-col sm:flex-row gap-3 max-w-xl"
          >
            <input
              data-testid="admin-secret-input"
              type="password"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="Admin secret"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              className="flex-1 bg-transparent border border-white/25 focus:border-[#F4C10A] outline-none px-4 py-3 font-mono text-sm text-white placeholder:text-white/40"
            />
            <button
              data-testid="admin-unlock-button"
              type="submit"
              disabled={loading || !secret.trim()}
              className="bg-[#F4C10A] text-black font-display uppercase text-sm tracking-[0.22em] px-6 py-3 hover:bg-[#FFD22C] disabled:opacity-50 transition-colors"
            >
              {loading ? "Verifying" : "Unlock"}
            </button>
          </form>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-4 border-b border-white/10">
              <div className="font-mono text-xs tracking-[0.2em] uppercase text-white/60">
                <span data-testid="admin-count" className="text-[#F4C10A]">{count}</span> Subscribers
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="admin-refresh"
                  onClick={() => load(secret.trim())}
                  className="border border-white/25 hover:border-[#F4C10A] hover:text-[#F4C10A] text-white font-mono text-[11px] tracking-[0.22em] uppercase px-4 py-2 transition-colors"
                >
                  Refresh
                </button>
                <button
                  data-testid="admin-download-csv"
                  onClick={downloadCsv}
                  className="bg-[#F4C10A] text-black font-display uppercase text-[11px] tracking-[0.22em] px-4 py-2 hover:bg-[#FFD22C] transition-colors"
                >
                  Export CSV
                </button>
                <button
                  data-testid="admin-logout"
                  onClick={logout}
                  className="text-white/50 hover:text-white font-mono text-[11px] tracking-[0.22em] uppercase px-2 py-2 transition-colors"
                >
                  Lock
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table data-testid="admin-subscribers-table" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/15">
                    <th className="text-left font-mono text-[10px] tracking-[0.28em] uppercase text-[#F4C10A] py-3 pr-4">Email</th>
                    <th className="text-left font-mono text-[10px] tracking-[0.28em] uppercase text-[#F4C10A] py-3 pr-4">Source</th>
                    <th className="text-left font-mono text-[10px] tracking-[0.28em] uppercase text-[#F4C10A] py-3 pr-4">Consent</th>
                    <th className="text-left font-mono text-[10px] tracking-[0.28em] uppercase text-[#F4C10A] py-3 pr-4">Signed Up</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-white/50 font-mono text-xs tracking-[0.2em] uppercase">
                        No subscribers yet
                      </td>
                    </tr>
                  ) : (
                    subscribers.map((s) => (
                      <tr key={s.id} className="border-b border-white/8">
                        <td className="py-3 pr-4 font-body text-white/90 break-all">{s.email}</td>
                        <td className="py-3 pr-4 font-mono text-xs text-white/60">{s.source}</td>
                        <td className="py-3 pr-4 font-mono text-xs text-white/60">{String(s.consent)}</td>
                        <td className="py-3 pr-4 font-mono text-xs text-white/60 whitespace-nowrap">{s.created_at}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {error && (
          <div data-testid="admin-error" className="mt-6 border border-red-500/40 bg-red-500/10 text-red-200 px-4 py-3 font-mono text-xs tracking-[0.15em] uppercase">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
